import type { FormikErrors, FormikProps } from 'formik'

import { FlowPage } from '@monthio/design-system'
import i18n from 'i18next'
import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import type { SmartCheckConfigurationDto } from '../../../types/authenticateSmartcheckSessionDto'
import type {
	CarLenderInputs,
	DefaultConsumerInputs,
	QuestionnaireStep,
	QuestionnaireStepValidationParameters,
} from '../types/questionnaire.types'

import { ScrollToViewContainer } from '../../../modulesTemp/shared/ScrollToViewContainer'
import { CarStatus } from '../types/questionnaire.types'
import { FormikCurrencyInput } from './formikIntegration'
import { FormikSingleSelect } from './formikIntegration/singleSelect'
import { InputSection } from './shared'

export const BoxInputWrapper = styled.div`
	width: 150px;
`

const getSelectedIsNewCar = (input: CarLenderInputs | undefined) => {
	if (input?.acquiringExtraCar == null) {
		return undefined
	}
	return input.acquiringExtraCar ? 'new' : 'replacement'
}

const getHasHadCarBefore = (input: CarLenderInputs | undefined) => {
	if (input?.hasHadCarBefore == null) {
		return undefined
	}
	// TODO: Why is it a string of a boolean? Refactor to boolean if possible
	return input.hasHadCarBefore.toString()
}

const getHasExpensesToMultipleCars = (input: CarLenderInputs | undefined) => {
	if (input?.hasExpensesToMultipleCars == null) {
		return undefined
	}
	// TODO: Why is it a string of a boolean? Refactor to boolean if possible
	return input.hasExpensesToMultipleCars.toString()
}

const getSelectedCarStatus = (input: CarLenderInputs | undefined) => {
	if (!input?.carStatus) {
		return undefined
	}
	return input.carStatus
}

const FormExtraCarExtendedStep = ({
	formProps,
	caseConfiguration,
}: {
	formProps: FormikProps<DefaultConsumerInputs>
	caseConfiguration?: SmartCheckConfigurationDto
}) => FormExtraCarBaseStep({ formProps }, caseConfiguration, true)

const FormExtraCarStep = ({
	formProps,
	caseConfiguration,
}: {
	formProps: FormikProps<DefaultConsumerInputs>
	caseConfiguration?: SmartCheckConfigurationDto
}) => FormExtraCarBaseStep({ formProps }, caseConfiguration, false)

const FormExtraCarBaseStep = (
	{
		formProps,
	}: {
		formProps: FormikProps<DefaultConsumerInputs>
	},
	caseConfiguration?: SmartCheckConfigurationDto,
	isExtended = false,
) => {
	const { t } = useTranslation()

	useEffect(() => {
		formProps.setSubmitting(false)
		formProps.setTouched({
			...formProps.touched,
			carLenderInputs: undefined,
		})
	}, [])

	const binaryChoices = useMemo(
		() => [
			{
				value: 'true',
				label: t('btn-yes'),
			},
			{
				value: 'false',
				label: t('btn-no'),
			},
		],
		[t],
	)

	const replacementChoices = useMemo(
		() => [
			{
				value: 'replacement',
				label: t('btn-yes'),
			},
			{
				value: 'new',
				label: t('btn-no'),
			},
		],
		[t],
	)

	const carStatusChoices = useMemo(
		() => [
			{
				value: CarStatus.Loan,
				label: t('consumerInputs-carCostStep-carStatusLoan-label'),
			},
			{
				value: CarStatus.Lease,
				label: t('consumerInputs-carCostStep-carStatusLease-label'),
			},
			{
				value: CarStatus.Private,
				label: t('consumerInputs-carCostStep-carStatusDebtFree-label'),
			},
		],
		[t],
	)

	// TODO: Validate if the types are lying or the optional chaining is unnecessary. Refactor if possible
	const isReplacement =
		formProps.values.carLenderInputs?.acquiringExtraCar == null
			? null
			: !formProps.values.carLenderInputs?.acquiringExtraCar
	// TODO: Validate if the types are lying or the optional chaining is unnecessary. Refactor if possible
	const hasMultipleCarExpenses =
		formProps.values.carLenderInputs?.hasExpensesToMultipleCars == null
			? null
			: formProps.values.carLenderInputs?.hasExpensesToMultipleCars

	const debtFree =
		formProps.values.carLenderInputs?.carStatus === CarStatus.Private

	const handleAcquiringExtraCarClick = useCallback(
		(value) => {
			formProps.setFieldValue(
				'carLenderInputs.acquiringExtraCar',
				value === 'new',
			)

			if (value === 'new') {
				clearReplacementState(formProps)
			}
		},
		[formProps.setFieldValue],
	)

	const cleanRelatedCarStatusFields = useCallback(
		(value) => {
			if (value !== CarStatus.Lease && value !== CarStatus.Loan) {
				clearLeasingState(formProps)
			}

			if (value !== CarStatus.Loan) {
				clearLoanState(formProps)
			}

			if (value !== CarStatus.Private) {
				clearPrivateState(formProps)
			}
		},
		[formProps],
	)

	const cleanRelatedMultipleCarsFields = useCallback(
		(value) => {
			if (value === 'false') {
				clearInsuranceState(formProps)
			}
		},
		[formProps],
	)

	const handleHasHadCarBeforeClick = useCallback(
		(value) => {
			formProps.setFieldValue(
				'carLenderInputs.hasHadCarBefore',
				value === 'true',
			)
		},
		[formProps.setFieldValue],
	)

	return (
		<FlowPage.Content $spacing="xl">
			<FlowPage.Header
				title={t('consumerInputs-carCostStep-additionalInformation-title')}
				description={t(
					'consumerInputs-carCostStep-additionalInformation-subtitle',
				)}
			/>
			<ScrollToViewContainer>
				<FormikSingleSelect
					fieldName="carLenderInputs.acquiringExtraCar"
					choicesOrientation="horizontal"
					choices={replacementChoices}
					selected={getSelectedIsNewCar(formProps.values.carLenderInputs)}
					onChange={handleAcquiringExtraCarClick}
					label={t('consumerInputs-carCostStep-acquiringExtraCar-text')}
					isRequired
				/>
			</ScrollToViewContainer>
			{isReplacement ? (
				<InputSection>
					<ScrollToViewContainer>
						<FormikSingleSelect
							fieldName="carLenderInputs.carStatus"
							label={t('consumerInputs-carCostStep-carStatus-text')}
							isRequired
							choices={carStatusChoices}
							selected={getSelectedCarStatus(formProps.values.carLenderInputs)}
							additionalOnChange={cleanRelatedCarStatusFields}
						/>
					</ScrollToViewContainer>
					{caseConfiguration?.shouldUseNewBudgetsFeature ? (
						<ScrollToViewContainer>
							<FormikSingleSelect
								fieldName="carLenderInputs.hasExpensesToMultipleCars"
								label={t(
									'consumerInputs-carCostStep-hasExpensesToMultipleCars-text',
								)}
								isRequired
								additionalOnChange={cleanRelatedMultipleCarsFields}
								choicesOrientation="horizontal"
								choices={binaryChoices}
								selected={getHasExpensesToMultipleCars(
									formProps.values.carLenderInputs,
								)}
							/>
						</ScrollToViewContainer>
					) : null}
					{hasMultipleCarExpenses === false &&
					caseConfiguration?.shouldUseNewBudgetsFeature ? (
						<ScrollToViewContainer>
							<FormikCurrencyInput
								fieldName="carLenderInputs.insurance"
								label={t('consumerInputs-carCostStep-insurance-text')}
								isRequired
							/>
						</ScrollToViewContainer>
					) : null}
					{!debtFree && !caseConfiguration?.shouldUseNewBudgetsFeature ? (
						<ScrollToViewContainer>
							<FormikCurrencyInput
								fieldName="carLenderInputs.currentMonthlyCarCost"
								label={t(
									'consumerInputs-carCostStep-currentMonthlyCarCost-text',
								)}
								isRequired
							/>
						</ScrollToViewContainer>
					) : null}
					{!debtFree &&
					formProps.values.carLenderInputs?.carStatus === CarStatus.Loan ? (
						<ScrollToViewContainer>
							<FormikCurrencyInput
								fieldName="carLenderInputs.redemptionAmount"
								label={t('consumerInputs-carCostStep-redemptionAmount-text')}
								isRequired
							/>
						</ScrollToViewContainer>
					) : null}
				</InputSection>
			) : null}
			{isExtended ? (
				<ScrollToViewContainer>
					<FormikSingleSelect
						fieldName="carLenderInputs.hasHadCarBefore"
						label={t('consumerInputs-carCostStep-hasHadCarBefore-title')}
						isRequired
						choicesOrientation="horizontal"
						choices={binaryChoices}
						selected={getHasHadCarBefore(formProps.values.carLenderInputs)}
						onChange={handleHasHadCarBeforeClick}
					/>
				</ScrollToViewContainer>
			) : null}
		</FlowPage.Content>
	)
}

const FormExtraCarExtendedStepValidation = async (
	validationParameters: QuestionnaireStepValidationParameters,
): Promise<FormikErrors<DefaultConsumerInputs>> =>
	FormExtraCarStepValidation(validationParameters, true)

const FormExtraCarStepValidation = async (
	validationParameters: QuestionnaireStepValidationParameters,
	isExtended = false,
): Promise<FormikErrors<DefaultConsumerInputs>> => {
	const { values, errors, caseConfiguration } = validationParameters
	if (values.carLenderInputs == null) {
		return errors
	}
	const requiredFieldText = i18n.t('validation-fieldRequired')

	if (values.carLenderInputs.acquiringExtraCar == null) {
		errors.carLenderInputs = {
			...errors.carLenderInputs,
			acquiringExtraCar: requiredFieldText,
		}
	}

	const debtFree = values.carLenderInputs.carStatus === CarStatus.Private
	const isReplacement = values.carLenderInputs.acquiringExtraCar === false
	const isLoan = values.carLenderInputs.carStatus === CarStatus.Loan
	const hasMultipleCars =
		values.carLenderInputs.hasExpensesToMultipleCars === true

	if (isReplacement) {
		values.carLenderInputs.hasHadCarBefore = true
	}

	if (
		isReplacement &&
		(!values.carLenderInputs.carStatus ||
			values.carLenderInputs.carStatus === CarStatus.None)
	) {
		errors.carLenderInputs = {
			...errors.carLenderInputs,
			carStatus: requiredFieldText,
		}
	}

	if (
		isReplacement &&
		caseConfiguration?.shouldUseNewBudgetsFeature &&
		values.carLenderInputs.hasExpensesToMultipleCars == null
	) {
		errors.carLenderInputs = {
			...errors.carLenderInputs,
			hasExpensesToMultipleCars: requiredFieldText,
		}
	}
	if (
		isReplacement &&
		caseConfiguration?.shouldUseNewBudgetsFeature &&
		!hasMultipleCars &&
		values.carLenderInputs.insurance == null
	) {
		errors.carLenderInputs = {
			...errors.carLenderInputs,
			insurance: requiredFieldText,
		}
	}

	if (
		isReplacement &&
		isLoan &&
		values.carLenderInputs.redemptionAmount == null
	) {
		errors.carLenderInputs = {
			...errors.carLenderInputs,
			redemptionAmount: requiredFieldText,
		}
	}

	if (
		isReplacement &&
		!debtFree &&
		!caseConfiguration?.shouldUseNewBudgetsFeature &&
		values.carLenderInputs.currentMonthlyCarCost == null
	) {
		errors.carLenderInputs = {
			...errors.carLenderInputs,
			currentMonthlyCarCost: requiredFieldText,
		}
	}

	if (
		!isReplacement &&
		isExtended &&
		values.carLenderInputs.hasHadCarBefore == null
	) {
		errors.carLenderInputs = {
			...errors.carLenderInputs,
			hasHadCarBefore: requiredFieldText,
		}
	}

	return errors
}

const clearReplacementState = (
	formProps: FormikProps<DefaultConsumerInputs>,
) => {
	clearCarStatusState(formProps)
	clearHasExpensesToMultipleCarsState(formProps)
	clearInsuranceState(formProps)
	clearLoanState(formProps)
	clearLeasingState(formProps)
	clearPrivateState(formProps)
}

const clearCarStatusState = (formProps: FormikProps<DefaultConsumerInputs>) => {
	formProps.setFieldValue('carLenderInputs.carStatus', CarStatus.None, false)
	formProps.setFieldTouched('carLenderInputs.carStatus', false, false)
}

const clearLoanState = (formProps: FormikProps<DefaultConsumerInputs>) => {
	formProps.setFieldValue('carLenderInputs.redemptionAmount', null, false)
	formProps.setFieldTouched('carLenderInputs.redemptionAmount', false, false)
}

const clearLeasingState = (formProps: FormikProps<DefaultConsumerInputs>) => {
	formProps.setFieldValue('carLenderInputs.currentMonthlyCarCost', null, false)
	formProps.setFieldTouched(
		'carLenderInputs.currentMonthlyCarCost',
		false,
		false,
	)
}

const clearPrivateState = (formProps: FormikProps<DefaultConsumerInputs>) => {
	formProps.setFieldValue('carLenderInputs.hasHadCarBefore', null, false)
	formProps.setFieldTouched('carLenderInputs.hasHadCarBefore', false, false)
}

const clearInsuranceState = (formProps: FormikProps<DefaultConsumerInputs>) => {
	formProps.setFieldValue('carLenderInputs.insurance', null, false)
	formProps.setFieldTouched('carLenderInputs.insurance', false, false)
}

const clearHasExpensesToMultipleCarsState = (
	formProps: FormikProps<DefaultConsumerInputs>,
) => {
	formProps.setFieldValue(
		'carLenderInputs.hasExpensesToMultipleCars',
		undefined,
		false,
	)
	formProps.setFieldTouched(
		'carLenderInputs.hasExpensesToMultipleCars',
		false,
		false,
	)
}

export const CarCostStep: QuestionnaireStep = {
	validation: FormExtraCarStepValidation,
	component: FormExtraCarStep,
}

export const CarCostExtendedStep: QuestionnaireStep = {
	validation: FormExtraCarExtendedStepValidation,
	component: FormExtraCarExtendedStep,
}
